import type { DialogProps } from './Dialog.types';
import type { ReactElement } from 'react';

import { Box, Flex, IconButton, Dialog as RadixDialog, ScrollArea } from '@rango-dev/whale';
import { X } from 'lucide-react';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { getDialogMaxWidth } from './Dialog.helpers';
import styles from './Dialog.module.css';

/**
 * Desktop branch of `Dialog` — a centered Radix Themes Dialog (used as-is, no
 * visual override). Header (title + close ✕), description, a scrolling content
 * slot, and a right-aligned footer, capped at 90vh.
 */
export function DesktopDialog(props: DialogProps): ReactElement {
  const maxWidth = getDialogMaxWidth(props.width);
  const autoId = useId();
  const { t } = useTranslation();
  const idPrefix = props.idPrefix ?? `dialog-${autoId.replace(/[^a-z0-9]/gi, '')}`;
  // Opt out of Radix's aria-describedby warning when there's no description.
  const describedBy = props.description ? {} : { 'aria-describedby': undefined };
  const scrollBody = props.scrollBody ?? true;

  let body: ReactElement | null = null;
  if (props.children) {
    body = scrollBody ? (
      <ScrollArea scrollbars="vertical" className={styles.body}>
        {props.children}
      </ScrollArea>
    ) : (
      <Box className={styles.body}>{props.children}</Box>
    );
  }

  return (
    <RadixDialog.Root
      open={props.open}
      defaultOpen={props.defaultOpen}
      onOpenChange={props.onOpenChange}>
      {props.trigger ? <RadixDialog.Trigger>{props.trigger}</RadixDialog.Trigger> : null}
      <RadixDialog.Content
        maxWidth={maxWidth}
        className={scrollBody ? styles.content : styles.contentStatic}
        {...describedBy}>
        <Flex align="start" justify="between" gap="3">
          <Flex direction="column" gap="1" minWidth="0">
            <RadixDialog.Title mb="0" size="5" weight="bold" truncate>
              {props.title}
            </RadixDialog.Title>
            {props.description ? (
              <RadixDialog.Description mb="0" size="2" color="gray">
                {props.description}
              </RadixDialog.Description>
            ) : null}
          </Flex>
          <RadixDialog.Close>
            <IconButton
              id={`${idPrefix}-close-btn`}
              type="button"
              variant="ghost"
              size="1"
              color="gray"
              aria-label={props.closeLabel ?? t('Close')}>
              <X size={16} aria-hidden />
            </IconButton>
          </RadixDialog.Close>
        </Flex>
        {body}
        {props.footer ? (
          <Flex justify="end" gap="3" wrap="wrap">
            {props.footer}
          </Flex>
        ) : null}
      </RadixDialog.Content>
    </RadixDialog.Root>
  );
}
